import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { TextField } from '@/components/forms/TextField';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { UpdateEmailSettingsDto } from '@/api/types/settingManagement';
import { useEmailSettings, useUpdateEmailSettings } from './api';
import { SendTestEmailCard } from './SendTestEmailCard';

interface EmailingTabProps {
  canChange: boolean;
  canSendTest: boolean;
}

const schema = z.object({
  smtpHost: z.string().trim(),
  smtpPort: z.number().int().min(0),
  smtpUserName: z.string().trim(),
  smtpPassword: z.string(),
  smtpDomain: z.string().trim(),
  smtpEnableSsl: z.boolean(),
  smtpUseDefaultCredentials: z.boolean(),
  defaultFromAddress: z.string().trim(),
  defaultFromDisplayName: z.string().trim(),
});

type EmailFormValues = z.infer<typeof schema>;

// Mirrors the ABP SettingManagement "Email settings" tab: the SMTP settings
// form (Save) plus the "Send test email" card (gated by Emailing.Test).
export function EmailingTab({ canChange, canSendTest }: EmailingTabProps) {
  const toast = useToast();
  const settingsQuery = useEmailSettings();
  const updateMut = useUpdateEmailSettings();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      smtpHost: '',
      smtpPort: 25,
      smtpUserName: '',
      smtpPassword: '',
      smtpDomain: '',
      smtpEnableSsl: false,
      smtpUseDefaultCredentials: true,
      defaultFromAddress: '',
      defaultFromDisplayName: '',
    },
  });

  useEffect(() => {
    if (settingsQuery.data) reset(settingsQuery.data);
  }, [settingsQuery.data, reset]);

  if (settingsQuery.isLoading) return <Spinner />;
  if (settingsQuery.isError)
    return (
      <ErrorState message="Could not load the email settings." onRetry={() => settingsQuery.refetch()} />
    );

  function onSubmit(values: EmailFormValues) {
    const input: UpdateEmailSettingsDto = { ...values };
    updateMut.mutate(input, {
      onSuccess: () => toast.success('Saved successfully.'),
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Failed to save the email settings.'),
    });
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="Smtp Host"
          disabled={!canChange}
          error={errors.smtpHost?.message}
          {...register('smtpHost')}
        />
        <TextField
          label="Port"
          type="number"
          disabled={!canChange}
          error={errors.smtpPort?.message}
          {...register('smtpPort', { valueAsNumber: true })}
        />

        <div className="mb-3 form-check">
          <input
            id="EmailSettings_SmtpEnableSsl"
            type="checkbox"
            className="form-check-input"
            disabled={!canChange}
            {...register('smtpEnableSsl')}
          />
          <label htmlFor="EmailSettings_SmtpEnableSsl" className="form-check-label">
            Enable Ssl
          </label>
        </div>

        <div className="mb-3 form-check">
          <input
            id="EmailSettings_SmtpUseDefaultCredentials"
            type="checkbox"
            className="form-check-input"
            disabled={!canChange}
            {...register('smtpUseDefaultCredentials')}
          />
          <label htmlFor="EmailSettings_SmtpUseDefaultCredentials" className="form-check-label">
            Use default credentials
          </label>
        </div>

        <TextField
          label="Domain"
          disabled={!canChange}
          error={errors.smtpDomain?.message}
          {...register('smtpDomain')}
        />
        <TextField
          label="User name"
          disabled={!canChange}
          autoComplete="off"
          error={errors.smtpUserName?.message}
          {...register('smtpUserName')}
        />
        <TextField
          label="Password"
          type="password"
          disabled={!canChange}
          autoComplete="new-password"
          error={errors.smtpPassword?.message}
          {...register('smtpPassword')}
        />
        <TextField
          label="Default from address"
          disabled={!canChange}
          error={errors.defaultFromAddress?.message}
          {...register('defaultFromAddress')}
        />
        <TextField
          label="Default from display name"
          disabled={!canChange}
          error={errors.defaultFromDisplayName?.message}
          {...register('defaultFromDisplayName')}
        />

        {canChange && (
          <button type="submit" className="btn btn-primary" disabled={updateMut.isPending}>
            {updateMut.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </form>

      {canSendTest && <SendTestEmailCard />}
    </div>
  );
}
